import { useState, useMemo } from 'react'
import { Link, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import RecordPaymentModal from '@/components/RecordPaymentModal'
import { LoanBadge } from '@/lib/loanIdentifiers'
import type { BorrowerWithLoans } from '@/types/loan'

interface PaymentItem {
  id: number
  amount: number
  date: string
  principal_portion: number
  interest_portion: number
  late_fee: number
  note: string | null
  loan_id: number
  loan_number: number
  borrower_id: number | null
  borrower_name: string
  updated_at: string | null
  edited: boolean
}

interface LoanOption {
  id: number
  loan_number: number
  borrower_name: string
}

interface Props {
  payments: PaymentItem[]
  loans: LoanOption[]
  borrowers_with_loans: BorrowerWithLoans[]
  stats: {
    total_collected_month: number
    interest_earned_month: number
    principal_returned_month: number
  }
  can_export_csv?: boolean
}

export default function PaymentsIndex({ payments, loans, borrowers_with_loans, stats, can_export_csv = false }: Props) {
  const [modalOpen, setModalOpen] = useState(false)
  const [editingPayment, setEditingPayment] = useState<PaymentItem | null>(null)
  const [filterLoan, setFilterLoan] = useState<number | ''>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const filtered = useMemo(() => {
    let result = payments

    if (filterLoan) {
      result = result.filter((p) => p.loan_id === filterLoan)
    }
    if (dateFrom) {
      result = result.filter((p) => p.date >= dateFrom)
    }
    if (dateTo) {
      result = result.filter((p) => p.date <= dateTo)
    }

    return result
  }, [payments, filterLoan, dateFrom, dateTo])

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
            <p className="text-sm text-gray-500 mt-1">{payments.length} total payments</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {payments.length > 0 && (
              can_export_csv ? (
                <a
                  href="/payments/export_csv"
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  <span className="hidden sm:inline">Export CSV</span>
                </a>
              ) : (
                <button
                  disabled
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gray-100 text-gray-400 text-sm font-medium rounded-lg cursor-not-allowed"
                  title="CSV export requires the Pro plan"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                  Pro Feature
                </button>
              )
            )}
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Record Payment
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Collected This Month" value={stats.total_collected_month} />
          <StatCard label="Interest Earned This Month" value={stats.interest_earned_month} color="text-indigo-600" />
          <StatCard label="Principal Returned This Month" value={stats.principal_returned_month} color="text-emerald-600" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <select
            value={filterLoan}
            onChange={(e) => setFilterLoan(Number(e.target.value) || '')}
            className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-600"
          >
            <option value="">All Loans</option>
            {loans.map((l) => (
              <option key={l.id} value={l.id}>{l.borrower_name} — Loan #{l.loan_number}</option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-600"
            />
            <span className="text-gray-400 text-xs">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-600"
            />
          </div>

          {(filterLoan || dateFrom || dateTo) && (
            <button
              onClick={() => { setFilterLoan(''); setDateFrom(''); setDateTo('') }}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-2"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Payments Table */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No payments yet</h3>
            <p className="text-sm text-gray-500 mb-6">Record your first payment to start tracking.</p>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Record Payment
            </button>
          </div>
        ) : (
          <>
          {/* Mobile: Card layout */}
          <div className="sm:hidden space-y-3">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Link
                      href={p.borrower_id ? `/borrowers/${p.borrower_id}` : `/loans/${p.loan_id}`}
                      className="text-sm font-medium text-emerald-600 hover:text-emerald-700 truncate"
                    >
                      {p.borrower_name}
                    </Link>
                    <LoanBadge loanNumber={p.loan_number} />
                  </div>
                  <button
                    onClick={() => setEditingPayment(p)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg min-w-[36px] min-h-[36px] flex items-center justify-center"
                    title="Edit payment"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">
                    {p.date}
                    {p.edited && (
                      <span className="ml-1.5 text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                        edited
                      </span>
                    )}
                  </span>
                  <span className="text-base font-mono font-bold text-gray-900">
                    ${p.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex gap-4 text-xs">
                  <span className="text-emerald-600 font-mono">
                    P: ${p.principal_portion.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                  <span className="text-indigo-500 font-mono">
                    I: ${p.interest_portion.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                  {p.note && <span className="text-gray-400 truncate flex-1">{p.note}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table layout */}
          <div className="hidden sm:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Borrower</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Date</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Amount</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Principal</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Interest</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Note</th>
                    <th className="w-10 py-3 px-4" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50/50 group">
                      <td className="py-2.5 px-4">
                        <div className="flex items-center gap-2">
                          <Link
                            href={p.borrower_id ? `/borrowers/${p.borrower_id}` : `/loans/${p.loan_id}`}
                            className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
                          >
                            {p.borrower_name}
                          </Link>
                          <LoanBadge loanNumber={p.loan_number} />
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-gray-600">
                        <span className="flex items-center gap-1.5">
                          {p.date}
                          {p.edited && (
                            <span
                              className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full"
                              title={p.updated_at ? `Last edited ${new Date(p.updated_at).toLocaleString()}` : 'Edited'}
                            >
                              edited
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono font-semibold text-gray-900">
                        ${p.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono text-emerald-600">
                        ${p.principal_portion.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2.5 px-4 text-right font-mono text-indigo-500">
                        ${p.interest_portion.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2.5 px-4 text-gray-400 text-xs max-w-[200px] truncate">
                        {p.note || '—'}
                      </td>
                      <td className="py-2.5 px-4">
                        <button
                          onClick={() => setEditingPayment(p)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                          title="Edit payment"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}
      </div>

      <RecordPaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        borrowersWithLoans={borrowers_with_loans}
      />

      {editingPayment && (
        <RecordPaymentModal
          open={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          payment={editingPayment}
          loanId={editingPayment.loan_id}
          borrowersWithLoans={borrowers_with_loans}
        />
      )}
    </AppLayout>
  )
}

function StatCard({ label, value, color = 'text-gray-900' }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold font-mono ${color}`}>
        ${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
      </p>
    </div>
  )
}
